// Shared data shapes: what feeds produce, what analytics compute, what the UI draws.

/** One intraday bar (5 minutes on NSE). */
export interface Bar {
  o: number;
  h: number;
  l: number;
  c: number;
  /** Shares traded in the bar. */
  v: number;
  /** Open interest of the stock's current-month future at the bar's close (0 when it has none). */
  oi: number;
  /** Estimated buyer-initiated volume; absent where order flow wasn't measured. */
  bv?: number;
  /** Estimated seller-initiated volume; absent where order flow wasn't measured. */
  sv?: number;
  /** Resting bid quantity in the order book at the bar's last tick. */
  bq?: number;
  /** Resting ask quantity in the order book at the bar's last tick. */
  sq?: number;
}

/** One stock's session: reference values from previous days plus today's bars. */
export interface StockSession {
  symbol: string;
  sector: string;
  prevClose: number;
  avgDayVolume: number;
  /** Previous session's closing open interest (0 when the stock has no future). */
  prevOI: number;
  prevHigh?: number;
  prevLow?: number;
  /** Usual daily high–low range, as % of price. */
  avgDayRangePct?: number;
  /**
   * Bar index where the closing auction starts (NSE F&O stocks from 3 Aug 2026: continuous trading
   * ends 15:15). Bars from here have no continuous trades, so patterns ignore them.
   */
  auctionFrom?: number;
  bars: Bar[];
}

export interface IndexQuote {
  id: string;
  name: string;
  members: number;
  loaded: number;
  ltp: number | null;
  prevClose: number | null;
}

export interface Session {
  source: 'simulated' | 'live';
  market: string;
  date: string;
  seed?: number;
  updatedAt?: string;
  index?: IndexQuote | null;
  stocks: StockSession[];
}

export interface DailyCandle {
  date: string;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export type Side = 'bull' | 'bear';

/** A return into a zone after it was confirmed, and what price did next. */
export interface ZoneTouch {
  /** Bar that first traded back into the zone. */
  idx: number;
  /** reacted: within the touch candle and the next 3, a close moved at least half a usual candle away
   * from the zone; failed: that didn't happen (zone still valid); testing: still being decided. */
  outcome: 'testing' | 'reacted' | 'failed';
  /** Bar where the outcome was decided. */
  resolvedIdx?: number;
}

export interface OrderBlock {
  type: Side;
  /** The source candle (the last opposite candle before the move). */
  idx: number;
  /** Zone: the source candle's full range, or its body when the candle is "wide". */
  top: number;
  bottom: number;
  /** The source candle was over 3× a usual candle, so the zone is its body, not its full range. */
  wide: boolean;
  /** The source candle's full range (wick to wick). */
  fullTop: number;
  fullBottom: number;
  /** Bar whose close broke structure: the zone exists from here (nothing earlier is known live). */
  confirmIdx: number;
  /** The level that close broke (a swing high/low or the opening range). */
  bosLevel: number;
  bosKind: 'swing' | 'opening-range';
  /** The furthest close beyond the source candle up to confirmation, in usual candles; negative when
   * price hadn't closed beyond it and a price gap proved the move. And whether it left a gap. */
  displacement: number;
  gap: boolean;
  /** Volume of the move (source to confirmation) vs the average candle before it today; null early on. */
  volRatio: number | null;
  /** The candle size used for this zone's thresholds (fixed at confirmation). */
  unit: number;
  /** Confirmed in the session's last 30 minutes of continuous trading (set by analyseStock). */
  late?: boolean;
  /** Broken: a finished candle closed beyond the far edge of the zone. */
  mitigated: boolean;
  endIdx?: number;
  /** Returns into the zone after confirmation, oldest first. */
  touches: ZoneTouch[];
  /** touches.length, and the first one's bar (kept for older readers). */
  retests: number;
  firstRetestIdx?: number;
}

/** Where a zone is in its life: fresh → testing → reacted / failed (each return) → broken. */
export type ZoneState = 'fresh' | 'testing' | 'reacted' | 'failed' | 'broken';
export const zoneState = (o: OrderBlock): ZoneState =>
  o.mitigated ? 'broken' : o.touches.length === 0 ? 'fresh' : o.touches[o.touches.length - 1].outcome;

/** Short chart label: "OB fresh", "OB testing", "OB reacted ×2", "OB broken". */
export const zoneLabel = (o: OrderBlock) => {
  const st = zoneState(o), n = o.touches.length;
  return `OB ${st === 'failed' ? 'no reaction' : st}${n > 1 && st !== 'broken' ? ` ×${n}` : ''}${o.wide ? ' · wide' : ''}${o.late ? ' · late' : ''}`;
};

/** A setup planned from the zone: enter at Z, wrong past the stop, targets ahead. */
export interface ZonePlan {
  side: Side;
  z: number;
  stop: number;
  t1: number;
  t2: number;
  /** What each target is (e.g. "equal highs", "swing high", "bearish order block"). */
  t1Name: string;
  t2Name: string;
  riskPerShare: number;
  /**
   * Reward per unit of risk when entering at Z, measured to `rrTo` (T2, or T1 when T2 is only
   * projected). null when there is no real level ahead at all.
   */
  rr: number | null;
  rrTo: 't1' | 't2';
  /** Price is already inside the zone. */
  inZone: boolean;
  /** Price's distance from Z, % of price (positive = price above Z). */
  distPct: number;
  state: ZoneState;
  retests: number;
}

export type LevelKey = 'PDH' | 'PDL' | 'ORH' | 'ORL' | 'HOD' | 'LOD' | 'EQH' | 'EQL';

export interface Sweep {
  /** bear = poked above a high and closed back below; bull = below a low and back above. */
  type: Side;
  key: LevelKey;
  name: string;
  level: number;
  idx: number;
  extreme: number;
  volMult: number | null;
  failed: boolean;
  failIdx?: number;
}

export interface Flow {
  /** First bar with measured order flow. */
  from: number;
  covered: number;
  delta: (number | null)[];
  cvd: (number | null)[];
  buyShare: number;
  deltaPct: number;
  recentPct: number | null;
  bidPct: number | null;
}

export interface VolumeProfile {
  buy: number[];
  sell: number[];
}

export type BuildUp = 'Long build-up' | 'Short covering' | 'Short build-up' | 'Long unwinding' | 'No F&O data';

/** A stock analysed at bar t. */
export interface Analysed {
  /** Thinly traded (a usual candle trades under ₹40 lakh): zones are easily made by one order. */
  thin: boolean;
  symbol: string;
  sector: string;
  prevClose: number;
  open: number;
  high: number;
  low: number;
  ltp: number;
  pct: number;
  bull: boolean;
  /** % change path: index 0 is the previous close (0%), then each bar's close. */
  series: number[];
  vwap: number;
  vwapHold: number;
  relVol: number;
  hasOI: boolean;
  oiChg: number;
  buildUp: BuildUp;
  score: number;
  moveRatio: number | null;
  /** Why levels are withheld (an unusual, likely news-driven move), or null. */
  eventRisk: string | null;
  obs: OrderBlock[];
  sweeps: Sweep[];
  flow: Flow | null;
  profile: VolumeProfile;
  // Filled by snapshot() once sector breadth is known.
  sectorAvg: number;
  sectorAdv: number;
  sectorN: number;
  withSector: boolean;
}

export interface SectorStat {
  name: string;
  avg: number;
  adv: number;
  n: number;
}

export interface Snapshot {
  t: number;
  time: string;
  stocks: Analysed[];
  sectors: SectorStat[];
  bulls: Analysed[];
  bears: Analysed[];
  topBulls: Analysed[];
  topBears: Analysed[];
}

export type SRKey = 'R1' | 'R2' | 'S1' | 'S2';

export interface LiquidityLevel {
  key: 'PDH' | 'PDL' | 'ORH' | 'ORL' | 'RND' | 'EQH' | 'EQL';
  name: string;
  p: number;
  distPct: number;
}

/** Everything a stock page needs beyond the board's per-stock analysis. */
export interface Detail {
  bars: Bar[];
  vwap: number[];
  atr: number;
  levels: Record<SRKey, number>;
  projected: Set<SRKey>;
  zone: OrderBlock | null;
  stop: number;
  target: number;
  risk: number;
  rr: number;
  percentile: number;
  liquidity: LiquidityLevel[];
  /** The setup if you wait for the zone; null without an active zone or on event risk. */
  plan: ZonePlan | null;
}
